<template>
  <div class="clases-page">
    <div class="bg-grid" aria-hidden="true"></div>

    <div class="clases-shell">
      <!-- Header -->
      <header class="page-header fade-in">
        <div class="page-header-left">
          <div class="page-icon-box">
            <v-icon color="#F2F2ED" size="20">mdi-calendar-month</v-icon>
          </div>
          <div>
            <h1 class="page-title">Gestión de clases</h1>
            <p class="page-sub">Calendario, horarios, instructores y salas</p>
          </div>
        </div>

        <div class="header-actions">
          <button class="btn-ghost" @click="dialogNuevoHorario = true">
            <v-icon size="15" class="mr-2">mdi-clock</v-icon>
            Programar horario
          </button>
          <button class="btn-ghost" @click="abrirGestionInstructores">
            <v-icon size="15" class="mr-2">mdi-account-tie</v-icon>
            Instructores
          </button>
          <button class="btn-ghost" @click="abrirGestionSalas">
            <v-icon size="15" class="mr-2">mdi-door-open</v-icon>
            Salas
          </button>
          <button class="btn-primary" @click="dialogNuevaClase = true">
            <v-icon size="15" class="mr-2">mdi-plus</v-icon>
            Nueva clase
          </button>
        </div>
      </header>

      <!-- Controles del calendario -->
      <section class="fade-in delay-100">
        <div class="card-flat controls-card">
          <div class="calendar-controls">
            <div class="calendar-nav">
              <button class="nav-btn" @click="navegarCalendario('prev')">
                <v-icon size="18">mdi-chevron-left</v-icon>
              </button>
              <button class="btn-ghost btn-sm" @click="hoy()">Hoy</button>
              <button class="nav-btn" @click="navegarCalendario('next')">
                <v-icon size="18">mdi-chevron-right</v-icon>
              </button>
            </div>

            <div class="calendar-date-display">
              <v-icon size="14" class="mr-2" color="#C8FF4D">mdi-calendar</v-icon>
              <span class="date-text">{{ rangoFechasTexto }}</span>
            </div>

            <button class="btn-ghost btn-sm toggle-btn" @click="mostrarListaClases = !mostrarListaClases">
              <v-icon size="15" class="mr-2">
                {{ mostrarListaClases ? 'mdi-calendar' : 'mdi-format-list-bulleted' }}
              </v-icon>
              {{ mostrarListaClases ? 'Ver calendario' : 'Ver lista' }}
            </button>
          </div>
        </div>
      </section>

      <!-- Lista o Calendario -->
      <section class="fade-in delay-200 mt-lg">
        <lista-clases-programadas v-if="mostrarListaClases" @ver-detalle="mostrarDetallesDesdeLista" />

        <div v-else class="card-flat calendar-card">
          <v-calendar ref="calendar" v-model="fechaActual" :events="horarios" :event-color="getEventColor"
            type="month" @click:event="mostrarDetalleEvento" class="dark-calendar" />
        </div>
      </section>
    </div>

    <!-- DIALOG: Gestión de Salas -->
    <v-dialog v-model="dialogGestionSalas" max-width="600px" persistent>
      <div class="modal-panel">
        <div class="modal-head">
          <h3 class="modal-title">Gestión de salas</h3>
        </div>

        <v-tabs v-model="tabSalas" grow class="dark-tabs">
          <v-tab>Lista de salas</v-tab>
          <v-tab>{{ salaEditando ? 'Editar' : 'Nueva' }} sala</v-tab>
        </v-tabs>

        <v-tabs-items v-model="tabSalas" class="dark-tabs-items">
          <!-- Lista -->
          <v-tab-item>
            <div class="tab-body">
              <div class="table-wrapper" v-if="salas.length > 0">
                <table class="plain-table">
                  <thead>
                    <tr>
                      <th>Nombre</th>
                      <th>Capacidad</th>
                      <th>Equipamiento</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="sala in salas" :key="sala.id">
                      <td class="cell-strong">{{ sala.nombre }}</td>
                      <td class="cell-secondary">{{ sala.capacidad }} personas</td>
                      <td class="cell-secondary">{{ sala.equipamiento || 'N/A' }}</td>
                      <td>
                        <div class="flex gap-xs">
                          <button class="icon-btn" @click="editarSala(sala)">
                            <v-icon size="15" color="#8C8C93">mdi-pencil</v-icon>
                          </button>
                          <button class="icon-btn icon-btn-danger" @click="confirmarEliminarSala(sala)">
                            <v-icon size="15" color="#FF6B6B">mdi-delete</v-icon>
                          </button>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div v-else class="empty-inline">Aún no hay salas registradas</div>
            </div>
          </v-tab-item>

          <!-- Formulario -->
          <v-tab-item>
            <div class="tab-body">
              <v-form ref="formSala" class="dark-form">
                <div class="field">
                  <label class="field-label">Nombre de la sala</label>
                  <v-text-field v-model="nuevaSala.nombre" :rules="reglasRequerido" outlined dense
                    hide-details="auto" placeholder="Ej. Sala Principal" class="dark-input"></v-text-field>
                </div>

                <div class="field">
                  <label class="field-label">Capacidad</label>
                  <v-text-field v-model="nuevaSala.capacidad" type="number" min="1" :rules="reglasRequerido" outlined
                    dense hide-details="auto" placeholder="Número de personas" class="dark-input"></v-text-field>
                </div>

                <div class="field">
                  <label class="field-label">Equipamiento</label>
                  <v-textarea v-model="nuevaSala.equipamiento" outlined dense rows="3" hide-details="auto"
                    placeholder="Ej. Pesas, máquinas cardiovasculares, espejos…" class="dark-input"></v-textarea>
                </div>
              </v-form>
            </div>
          </v-tab-item>
        </v-tabs-items>

        <div class="modal-footer">
          <button class="btn-ghost" @click="cerrarDialogoSalas">Cancelar</button>
          <button v-if="tabSalas === 1" class="btn-primary" @click="guardarSala">
            {{ salaEditando ? 'Actualizar' : 'Guardar' }}
          </button>
          <button v-if="tabSalas === 0" class="btn-primary" @click="tabSalas = 1">
            <v-icon size="15" class="mr-2">mdi-plus</v-icon>
            Nueva sala
          </button>
        </div>
      </div>
    </v-dialog>

    <!-- DIALOG: Eliminar Sala -->
    <v-dialog v-model="dialogEliminarSala" max-width="420px">
      <div class="modal-panel">
        <div class="modal-head">
          <h3 class="modal-title">Confirmar eliminación</h3>
        </div>
        <div class="modal-body">
          <p class="modal-text">
            ¿Estás seguro de eliminar la sala
            <strong v-if="salaSeleccionado" class="text-error">{{ salaSeleccionado.nombre }}</strong>?
          </p>
        </div>
        <div class="modal-footer">
          <button class="btn-ghost" @click="dialogEliminarSala = false">Cancelar</button>
          <button class="btn-danger-solid" @click="eliminarSala">Eliminar</button>
        </div>
      </div>
    </v-dialog>

    <!-- DIALOG: Gestión de Instructores -->
    <v-dialog v-model="dialogGestionInstructores" max-width="640px" persistent>
      <div class="modal-panel">
        <div class="modal-head">
          <h3 class="modal-title">Gestión de instructores</h3>
        </div>

        <v-tabs v-model="tabInstructores" grow class="dark-tabs">
          <v-tab>Lista de instructores</v-tab>
          <v-tab>{{ instructorEditando ? 'Editar' : 'Nuevo' }} instructor</v-tab>
        </v-tabs>

        <v-tabs-items v-model="tabInstructores" class="dark-tabs-items">
          <!-- Lista -->
          <v-tab-item>
            <div class="tab-body">
              <div v-if="instructores.length > 0" class="instructores-grid">
                <div v-for="instructor in instructores" :key="instructor.id" class="instructor-card">
                  <div class="instructor-head">
                    <div class="instructor-avatar">
                      <v-icon size="16" color="#0E0E10">mdi-account</v-icon>
                    </div>
                    <div class="flex-1">
                      <h4 class="instructor-name">{{ instructor.nombre }}</h4>
                      <p class="instructor-specialty">
                        <v-icon size="11" class="mr-1">mdi-certificate</v-icon>
                        {{ instructor.especialidad || 'Sin especialidad' }}
                      </p>
                    </div>
                  </div>
                  <div class="instructor-info">
                    <span class="info-line">
                      <v-icon size="12" class="mr-1" color="#5C5C62">mdi-email</v-icon>
                      {{ instructor.email || 'Sin email' }}
                    </span>
                    <span class="info-line">
                      <v-icon size="12" class="mr-1" color="#5C5C62">mdi-phone</v-icon>
                      {{ instructor.telefono || 'Sin teléfono' }}
                    </span>
                  </div>
                  <div class="instructor-actions">
                    <button class="btn-ghost btn-sm" @click="editarInstructor(instructor)">
                      <v-icon size="13" class="mr-1">mdi-pencil</v-icon>
                      Editar
                    </button>
                    <button class="btn-text-danger" @click="confirmarEliminarInstructor(instructor)">
                      <v-icon size="13" class="mr-1">mdi-delete</v-icon>
                      Eliminar
                    </button>
                  </div>
                </div>
              </div>
              <div v-else class="empty-inline">Aún no hay instructores registrados</div>
            </div>
          </v-tab-item>

          <!-- Formulario -->
          <v-tab-item>
            <div class="tab-body">
              <v-form ref="formInstructor" class="dark-form">
                <div class="field">
                  <label class="field-label">Nombre completo</label>
                  <v-text-field v-model="nuevoInstructor.nombre" :rules="reglasRequerido" outlined dense
                    hide-details="auto" placeholder="Nombre del instructor" class="dark-input"></v-text-field>
                </div>

                <div class="field">
                  <label class="field-label">Especialidad</label>
                  <v-text-field v-model="nuevoInstructor.especialidad" outlined dense hide-details="auto"
                    placeholder="Ej. Yoga, Crossfit, Spinning" class="dark-input"></v-text-field>
                </div>

                <div class="field">
                  <label class="field-label">Email</label>
                  <v-text-field v-model="nuevoInstructor.email" type="email" outlined dense hide-details="auto"
                    placeholder="correo@ejemplo.com" class="dark-input"></v-text-field>
                </div>

                <div class="field">
                  <label class="field-label">Teléfono</label>
                  <v-text-field v-model="nuevoInstructor.telefono" type="tel" outlined dense hide-details="auto"
                    placeholder="(123) 456-7890" class="dark-input"></v-text-field>
                </div>
              </v-form>
            </div>
          </v-tab-item>
        </v-tabs-items>

        <div class="modal-footer">
          <button class="btn-ghost" @click="cerrarDialogoInstructores">Cancelar</button>
          <button v-if="tabInstructores === 1" class="btn-primary" @click="guardarInstructor">
            {{ instructorEditando ? 'Actualizar' : 'Guardar' }}
          </button>
          <button v-if="tabInstructores === 0" class="btn-primary" @click="tabInstructores = 1">
            <v-icon size="15" class="mr-2">mdi-plus</v-icon>
            Nuevo instructor
          </button>
        </div>
      </div>
    </v-dialog>

    <!-- DIALOG: Eliminar Instructor -->
    <v-dialog v-model="dialogEliminarInstructor" max-width="420px">
      <div class="modal-panel">
        <div class="modal-head">
          <h3 class="modal-title">Confirmar eliminación</h3>
        </div>
        <div class="modal-body">
          <p class="modal-text">
            ¿Estás seguro de eliminar al instructor
            <strong v-if="instructorSeleccionado" class="text-error">{{ instructorSeleccionado.nombre }}</strong>?
          </p>
        </div>
        <div class="modal-footer">
          <button class="btn-ghost" @click="dialogEliminarInstructor = false">Cancelar</button>
          <button class="btn-danger-solid" @click="eliminarInstructor">Eliminar</button>
        </div>
      </div>
    </v-dialog>

    <!-- DIALOG: Nueva Clase -->
    <v-dialog v-model="dialogNuevaClase" max-width="600px">
      <div class="modal-panel">
        <div class="modal-head">
          <h3 class="modal-title">Crear nueva clase</h3>
        </div>

        <div class="modal-body">
          <v-form ref="formClase" class="dark-form">
            <div class="field">
              <label class="field-label">Nombre de la clase</label>
              <v-text-field v-model="nuevaClase.nombre" :rules="reglasRequerido" outlined dense hide-details="auto"
                placeholder="Ej. Yoga Matutino" class="dark-input"></v-text-field>
            </div>

            <div class="field">
              <label class="field-label">Descripción</label>
              <v-textarea v-model="nuevaClase.descripcion" outlined dense rows="3" hide-details="auto"
                placeholder="Describe brevemente la clase…" class="dark-input"></v-textarea>
            </div>

            <div class="field-row">
              <div class="field">
                <label class="field-label">Nivel de dificultad</label>
                <v-select v-model="nuevaClase.nivel_dificultad" :items="nivelesDificultad" outlined dense
                  hide-details="auto" class="dark-input"></v-select>
              </div>
              <div class="field">
                <label class="field-label">Duración (minutos)</label>
                <v-text-field v-model="nuevaClase.duracion_min" type="number" min="15" max="180"
                  :rules="reglasRequerido" outlined dense hide-details="auto" placeholder="60"
                  class="dark-input"></v-text-field>
              </div>
            </div>

            <div class="field-row">
              <div class="field">
                <label class="field-label">Precio</label>
                <v-text-field v-model="nuevaClase.precio" type="number" min="0" step="0.01" :rules="reglasRequerido"
                  outlined dense hide-details="auto" prefix="$" placeholder="0.00" class="dark-input"></v-text-field>
              </div>
              <div class="field">
                <label class="field-label">Color en el calendario</label>
                <div class="color-picker-wrapper">
                  <input v-model="nuevaClase.color_calendario" type="color" class="color-input" />
                  <span class="color-preview" :style="{ background: nuevaClase.color_calendario }"></span>
                  <span class="color-value">{{ nuevaClase.color_calendario }}</span>
                </div>
              </div>
            </div>
          </v-form>
        </div>

        <div class="modal-footer">
          <button class="btn-ghost" @click="dialogNuevaClase = false">Cancelar</button>
          <button class="btn-primary" @click="guardarClase">
            <v-icon size="15" class="mr-2">mdi-check</v-icon>
            Guardar clase
          </button>
        </div>
      </div>
    </v-dialog>

    <!-- DIALOG: Nuevo Horario -->
    <v-dialog v-model="dialogNuevoHorario" max-width="620px">
      <div class="modal-panel">
        <div class="modal-head">
          <h3 class="modal-title">Programar nueva clase</h3>
        </div>

        <div class="modal-body">
          <v-form ref="formHorario" class="dark-form">
            <div class="field-row field-row-3">
              <div class="field">
                <label class="field-label">Clase</label>
                <v-select v-model="nuevoHorario.id_clase" :items="clases" item-text="nombre" item-value="id"
                  placeholder="Seleccionar" outlined dense hide-details="auto" class="dark-input"></v-select>
              </div>
              <div class="field">
                <label class="field-label">Instructor</label>
                <v-select v-model="nuevoHorario.id_instructor" :items="instructores" item-text="nombre"
                  item-value="id" placeholder="Seleccionar" outlined dense hide-details="auto"
                  class="dark-input"></v-select>
              </div>
              <div class="field">
                <label class="field-label">Sala</label>
                <v-select v-model="nuevoHorario.id_sala" :items="salas" item-text="nombre" item-value="id"
                  placeholder="Seleccionar" outlined dense hide-details="auto" class="dark-input"></v-select>
              </div>
            </div>

            <div class="field-row">
              <div class="field">
                <label class="field-label">Fecha de la clase</label>
                <v-menu ref="menuFechaHorario" v-model="menuFechaHorario" :close-on-content-click="false"
                  transition="scale-transition" offset-y min-width="auto">
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field v-model="nuevoHorario.fecha" readonly outlined dense hide-details="auto"
                      prepend-inner-icon="mdi-calendar" placeholder="Seleccionar fecha" v-bind="attrs" v-on="on"
                      class="dark-input"></v-text-field>
                  </template>
                  <v-date-picker v-model="nuevoHorario.fecha" no-title
                    :min="new Date().toISOString().substr(0, 10)" class="dark-picker"></v-date-picker>
                </v-menu>
              </div>

              <div class="field">
                <label class="field-label">Hora de inicio</label>
                <v-menu ref="menuHoraInicio" v-model="menuHoraInicio" :close-on-content-click="false"
                  transition="scale-transition" offset-y max-width="290px" min-width="290px">
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field v-model="nuevoHorario.hora_inicio" readonly outlined dense hide-details="auto"
                      prepend-inner-icon="mdi-clock" placeholder="Seleccionar hora" v-bind="attrs" v-on="on"
                      class="dark-input"></v-text-field>
                  </template>
                  <v-time-picker v-model="nuevoHorario.hora_inicio" format="24hr" full-width
                    class="dark-picker"></v-time-picker>
                </v-menu>
              </div>
            </div>

            <div class="field-row">
              <div class="field">
                <label class="field-label">Cupos máximos</label>
                <v-text-field v-model="nuevoHorario.max_participantes" type="number" min="1" max="50" outlined dense
                  hide-details="auto" placeholder="20" class="dark-input"></v-text-field>
              </div>
              <div class="field">
                <label class="field-label">Repetición</label>
                <v-select v-model="nuevoHorario.repeticion" :items="opcionesRepeticion" item-text="text"
                  item-value="value" outlined dense hide-details="auto" class="dark-input"></v-select>
              </div>
            </div>

            <div v-if="nuevoHorario.repeticion !== 'ninguna'" class="field">
              <label class="field-label">Número de repeticiones</label>
              <v-text-field v-model="nuevoHorario.veces" type="number" min="1" max="52" outlined dense
                hide-details="auto" placeholder="4" class="dark-input"></v-text-field>
              <p class="field-hint">¿Cuántas veces se repetirá?</p>
            </div>
          </v-form>
        </div>

        <div class="modal-footer">
          <button class="btn-ghost" @click="dialogNuevoHorario = false">Cancelar</button>
          <button class="btn-primary" @click="guardarHorario">
            <v-icon size="15" class="mr-2">mdi-calendar-check</v-icon>
            Programar
          </button>
        </div>
      </div>
    </v-dialog>

    <!-- DIALOG: Detalle de Clase -->
    <v-dialog v-model="dialogDetalleClase" max-width="620px">
      <div class="modal-panel">
        <div class="modal-head detail-head">
          <div>
            <h3 class="modal-title">{{ eventoSeleccionado.name }}</h3>
            <span class="event-color-dot" :style="{ background: eventoSeleccionado.color }"></span>
          </div>
          <span class="status-tag status-tag-success">${{ eventoSeleccionado.precio || 0 }}</span>
        </div>

        <div class="modal-body">
          <div class="clase-info-grid">
            <div class="info-card">
              <v-icon size="16" color="#C8FF4D">mdi-account-tie</v-icon>
              <div>
                <div class="info-label">Instructor</div>
                <div class="info-value">{{ eventoSeleccionado.instructor }}</div>
              </div>
            </div>
            <div class="info-card">
              <v-icon size="16" color="#C8FF4D">mdi-map-marker</v-icon>
              <div>
                <div class="info-label">Sala</div>
                <div class="info-value">{{ eventoSeleccionado.sala }}</div>
              </div>
            </div>
            <div class="info-card">
              <v-icon size="16" color="#C8FF4D">mdi-clock</v-icon>
              <div>
                <div class="info-label">Horario</div>
                <div class="info-value">
                  {{ formatoHora(eventoSeleccionado.start) }} – {{ formatoHora(eventoSeleccionado.end) }}
                </div>
              </div>
            </div>
            <div class="info-card">
              <v-icon size="16" color="#C8FF4D">mdi-timer</v-icon>
              <div>
                <div class="info-label">Duración</div>
                <div class="info-value">{{ eventoSeleccionado.duracion }} min</div>
              </div>
            </div>
            <div class="info-card">
              <v-icon size="16" color="#C8FF4D">mdi-chart-donut</v-icon>
              <div>
                <div class="info-label">Nivel</div>
                <div class="info-value text-capitalize">{{ eventoSeleccionado.nivel }}</div>
              </div>
            </div>
          </div>

          <div class="alert-box" :class="{ 'alert-box-warning': eventoSeleccionado.cuposDisponibles <= 0 }">
            <v-icon size="18" :color="eventoSeleccionado.cuposDisponibles > 0 ? '#8C8C93' : '#FFB84D'">
              {{ eventoSeleccionado.cuposDisponibles > 0 ? 'mdi-information-outline' : 'mdi-alert' }}
            </v-icon>
            <div class="alert-text">
              <strong>{{ eventoSeleccionado.cuposDisponibles }} / {{ eventoSeleccionado.cuposTotales }}</strong> cupos disponibles
            </div>
          </div>

          <!-- Gestión de miembros -->
          <v-expansion-panels class="dark-expansion mt-lg">
            <v-expansion-panel>
              <v-expansion-panel-header>
                <span class="expansion-title">
                  <v-icon size="16" class="mr-2">mdi-account-group</v-icon>
                  Gestionar miembros inscritos ({{ miembrosInscritos.length }})
                </span>
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <div class="field mb-lg">
                  <v-text-field v-model="busquedaMiembro" outlined dense hide-details="auto"
                    prepend-inner-icon="mdi-magnify" placeholder="Buscar miembro…" class="dark-input"></v-text-field>
                </div>

                <div v-if="miembrosInscritos.length > 0" class="miembros-list">
                  <div v-for="miembro in miembrosFiltrados" :key="miembro.id" class="miembro-item">
                    <div class="miembro-avatar">
                      <v-icon size="14" color="#0E0E10">mdi-account</v-icon>
                    </div>
                    <div class="flex-1">
                      <div class="miembro-name">{{ miembro.nombre }}</div>
                      <div class="miembro-meta">{{ miembro.email }} · {{ miembro.telefono }}</div>
                      <div class="miembro-date">Inscrito el {{ formatoFecha(miembro.fecha_reserva) }}</div>
                    </div>
                    <button class="icon-btn icon-btn-danger" @click="confirmarEliminarMiembro(miembro)">
                      <v-icon size="15" color="#FF6B6B">mdi-delete</v-icon>
                    </button>
                  </div>
                </div>
                <div v-else class="empty-inline">No hay miembros inscritos en esta clase</div>

                <div class="section-divider"></div>

                <v-form @submit.prevent="agregarMiembro" ref="formMiembro" class="add-member-row">
                  <div class="field field-grow">
                    <label class="field-label">Seleccionar miembro</label>
                    <v-autocomplete v-model="nuevoMiembro.id" :items="miembrosDisponibles" item-text="nombre"
                      item-value="id" placeholder="Buscar miembro…" outlined dense hide-details="auto"
                      class="dark-input">
                      <template v-slot:item="{ item }">
                        <span class="autocomplete-item-text">{{ item.matricula }} — {{ item.nombre }}</span>
                      </template>
                    </v-autocomplete>
                  </div>

                  <div class="field">
                    <label class="field-label">Monto pagado</label>
                    <v-text-field v-model="nuevoMiembro.monto_pagado" type="number" min="0" step="0.01" outlined
                      dense hide-details="auto" prefix="$" placeholder="0.00" class="dark-input"></v-text-field>
                  </div>

                  <button type="submit" class="btn-primary add-member-btn"
                    :disabled="eventoSeleccionado.cuposDisponibles <= 0">
                    <v-icon size="15" class="mr-2">mdi-plus</v-icon>
                    Agregar
                  </button>
                </v-form>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </div>

        <div class="modal-footer">
          <button class="btn-ghost" @click="dialogDetalleClase = false">Cerrar</button>
        </div>
      </div>
    </v-dialog>

    <!-- DIALOG: Eliminar Miembro de Clase -->
    <v-dialog v-model="dialogEliminarMiembro" max-width="420px">
      <div class="modal-panel">
        <div class="modal-head">
          <h3 class="modal-title">Confirmar eliminación</h3>
        </div>
        <div class="modal-body">
          <p class="modal-text">
            ¿Estás seguro de eliminar a
            <strong v-if="miembroSeleccionado" class="text-error">{{ miembroSeleccionado.nombre }}</strong>
            de esta clase?
          </p>
        </div>
        <div class="modal-footer">
          <button class="btn-ghost" @click="dialogEliminarMiembro = false">Cancelar</button>
          <button class="btn-danger-solid" @click="eliminarMiembro">Eliminar</button>
        </div>
      </div>
    </v-dialog>
  </div>
</template>

<script>
import HttpService from '../../Servicios/HttpService';
import ListaClasesProgramadas from './ListaClasesProgramadas.vue';

export default {
  name: 'GestionClases',
  components: {
    ListaClasesProgramadas
  },

  data: () => ({
    fechaActual: new Date(),
    vistaCalendario: 'month',
    rangoFechas: [],
    salaSeleccionada: [],
    menuFecha: false,
    dialogNuevaClase: false,
    dialogNuevoHorario: false,
    dialogDetalleClase: false,
    menuHoraInicio: false,
    reglasRequerido: [(value) => !!value || 'Campo obligatorio'],
    nuevaClase: {
      nombre: '',
      descripcion: '',
      duracion_min: 60,
      nivel_dificultad: 'intermedio',
      color_calendario: '#1976D2',
      precio: 0
    },
    nuevoHorario: {
      id_clase: null,
      id_instructor: null,
      id_sala: null,
      fecha: new Date().toISOString().substr(0, 10),
      hora_inicio: null,
      max_participantes: 20,
      repeticion: 'ninguna',
      veces: 4
    },
    menuFechaHorario: false,
    opcionesRepeticion: [
      { text: 'No repetir', value: 'ninguna' },
      { text: 'Diariamente', value: 'diaria' },
      { text: 'Semanalmente (mismo día)', value: 'semanal' },
      { text: 'Lunes a Viernes', value: 'lunes_viernes' },
      { text: 'Fines de semana', value: 'fin_semana' },
      { text: 'Mensualmente', value: 'mensual' }
    ],
    nivelesDificultad: ['principiante', 'intermedio', 'avanzado'],
    clases: [],
    instructores: [],
    salas: [],
    horarios: [],
    eventoSeleccionado: {},
    timeoutBusqueda: null,
    dialogGestionInstructores: false,
    dialogEliminarInstructor: false,
    tabInstructores: 0,
    nuevoInstructor: {
      nombre: '',
      especialidad: '',
      email: '',
      telefono: ''
    },
    instructorEditando: null,
    instructorSeleccionado: null,
    dialogGestionSalas: false,
    dialogEliminarSala: false,
    tabSalas: 0,
    nuevaSala: {
      nombre: '',
      capacidad: 20,
      equipamiento: ''
    },
    salaEditando: null,
    salaSeleccionado: null,
    miembrosInscritos: [],
    miembrosDisponibles: [],
    busquedaMiembro: '',
    nuevoMiembro: {
      id: null,
      monto_pagado: 0
    },
    miembroSeleccionado: null,
    dialogEliminarMiembro: false,
    mostrarListaClases: false,
  }),

  computed: {
    rangoFechasTexto() {
      if (this.vistaCalendario === 'month') {
        const start = new Date(this.rangoFechas[0]);
        return start.toLocaleDateString('es-ES', { month: 'long', year: 'numeric' });
      } else {
        const start = new Date(this.rangoFechas[0]);
        const end = new Date(this.rangoFechas[1]);
        const options = { day: 'numeric', month: 'short' };
        return `${start.toLocaleDateString('es-ES', options)} - ${end.toLocaleDateString('es-ES', options)}`;
      }
    },
    miembrosFiltrados() {
      if (!this.busquedaMiembro) return this.miembrosInscritos;

      return this.miembrosInscritos.filter(miembro =>
        miembro.nombre.toLowerCase().includes(this.busquedaMiembro.toLowerCase()) ||
        miembro.email.toLowerCase().includes(this.busquedaMiembro.toLowerCase())
      );
    }
  },

  watch: {
    vistaCalendario(newVal) {
      this.ajustarRangoFechas(newVal);
    },
    dialogDetalleClase(val) {
      if (val && this.eventoSeleccionado.id) {
        this.cargarMiembrosInscritos();
        this.cargarMiembrosDisponibles();
      }
    }
  },

  mounted() {
    this.ajustarRangoFechas(this.vistaCalendario);
    this.cargarDatosIniciales();
    this.cargarHorarios();
  },

  methods: {
    mostrarDetallesDesdeLista(clase) {
      this.eventoSeleccionado = {
        id: clase.id,
        name: clase.nombre_clase,
        start: new Date(clase.fecha_hora_inicio),
        end: new Date(clase.fecha_hora_fin),
        color: clase.color_calendario || '#1976D2',
        precio: clase.precio || 0,
        instructor: clase.nombre_instructor,
        sala: clase.nombre_sala,
        cuposDisponibles: clase.cupos_disponibles,
        cuposTotales: clase.max_participantes,
        duracion: clase.duracion_min,
        nivel: clase.nivel_dificultad
      };

      this.dialogDetalleClase = true;
    },

    navegarCalendario(direccion) {
      if (this.vistaCalendario === 'month') {
        this.navegarMes(direccion);
      } else {
        this.navegarSemana(direccion);
      }
    },

    navegarSemana(direccion) {
      const factor = direccion === 'next' ? 7 : -7;
      const start = new Date(this.rangoFechas[0]);
      const end = new Date(this.rangoFechas[1]);

      start.setDate(start.getDate() + factor);
      end.setDate(end.getDate() + factor);

      start.setHours(0, 0, 0, 0);
      end.setHours(23, 59, 59, 999);

      this.rangoFechas = [start, end];

      this.actualizarCalendario();
    },

    navegarMes(direccion) {
      const factor = direccion === 'next' ? 1 : -1;
      const start = new Date(this.rangoFechas[0]);
      start.setMonth(start.getMonth() + factor);
      this.ajustarRangoFechas('month', start);
    },

    hoy() {
      this.ajustarRangoFechas(this.vistaCalendario, new Date().toISOString().substr(0, 10));
    },

    ajustarRangoFechas(vista, fechaInicio = null) {
      const fechaBase = fechaInicio ? new Date(fechaInicio) : new Date();

      if (vista === 'month') {
        const firstDay = new Date(
          fechaBase.getFullYear(),
          fechaBase.getMonth(),
          1,
          0, 0, 0, 0
        );

        const lastDay = new Date(
          fechaBase.getFullYear(),
          fechaBase.getMonth() + 1,
          0,
          23, 59, 59, 999
        );

        this.rangoFechas = [firstDay, lastDay];
      } else {
        const dayOfWeek = fechaBase.getDay();
        const diffToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;

        const monday = new Date(fechaBase);
        monday.setDate(fechaBase.getDate() + diffToMonday);
        monday.setHours(0, 0, 0, 0);

        const sunday = new Date(monday);
        sunday.setDate(monday.getDate() + 6);
        sunday.setHours(23, 59, 59, 999);

        this.rangoFechas = [monday, sunday];
      }

      this.actualizarCalendario();
    }
    ,

    actualizarCalendario() {
      this.fechaActual = new Date(this.rangoFechas[0]);
      this.cargarHorarios();


    },

    // Gestión de Instructores
    abrirGestionInstructores() {
      this.dialogGestionInstructores = true;
      this.tabInstructores = 0;
      this.resetFormInstructor();
    },

    cerrarDialogoInstructores() {
      this.dialogGestionInstructores = false;
      this.instructorEditando = null;
      this.resetFormInstructor();
    },

    resetFormInstructor() {
      this.nuevoInstructor = {
        nombre: '',
        especialidad: '',
        email: '',
        telefono: ''
      };
      this.instructorEditando = null;
    },

    editarInstructor(instructor) {
      this.instructorEditando = instructor;
      this.nuevoInstructor = { ...instructor };
      this.tabInstructores = 1;
    },

    confirmarEliminarInstructor(instructor) {
      this.instructorSeleccionado = instructor;
      this.dialogEliminarInstructor = true;
    },

    async guardarInstructor() {
      if (!this.$refs.formInstructor.validate()) {
        this.$store.commit('mostrarMensaje', {
          texto: 'Por favor complete los campos requeridos',
          color: 'warning'
        });
        return;
      }

      try {
        const payload = {
          metodo: this.instructorEditando ? 'actualizar_instructor' : 'crear_instructor',
          instructor: this.nuevoInstructor
        };

        if (this.instructorEditando) {
          payload.instructor.id = this.instructorEditando.id;
        }

        await HttpService.registrar(payload, 'clases.php');

        this.$store.commit('mostrarMensaje', {
          texto: `Instructor ${this.instructorEditando ? 'actualizado' : 'creado'} exitosamente`,
          color: 'success'
        });

        this.cargarDatosIniciales();
        this.tabInstructores = 0;
        this.resetFormInstructor();

      } catch (error) {
        console.error('Error guardando instructor:', error);
        this.$store.commit('mostrarMensaje', {
          texto: 'Error al guardar instructor',
          color: 'error'
        });
      }
    },

    async eliminarInstructor() {
      try {
        const payload = {
          metodo: 'eliminar_instructor',
          id: this.instructorSeleccionado.id
        };

        await HttpService.eliminar('clases.php', payload);

        this.$store.commit('mostrarMensaje', {
          texto: 'Instructor eliminado exitosamente',
          color: 'success'
        });

        this.dialogEliminarInstructor = false;
        this.cargarDatosIniciales();

      } catch (error) {
        console.error('Error eliminando instructor:', error);
        this.$store.commit('mostrarMensaje', {
          texto: 'Error al eliminar instructor',
          color: 'error'
        });
      }
    },

    // Gestión de Salas
    abrirGestionSalas() {
      this.dialogGestionSalas = true;
      this.tabSalas = 0;
      this.resetFormSala();
    },

    cerrarDialogoSalas() {
      this.dialogGestionSalas = false;
      this.salaEditando = null;
      this.resetFormSala();
    },

    resetFormSala() {
      this.nuevaSala = {
        nombre: '',
        capacidad: 20,
        equipamiento: ''
      };
      this.salaEditando = null;
    },

    editarSala(sala) {
      this.salaEditando = sala;
      this.nuevaSala = { ...sala };
      this.tabSalas = 1;
    },

    confirmarEliminarSala(sala) {
      this.salaSeleccionado = sala;
      this.dialogEliminarSala = true;
    },

    async guardarSala() {
      if (!this.$refs.formSala.validate()) {
        this.$store.commit('mostrarMensaje', {
          texto: 'Por favor complete los campos requeridos',
          color: 'warning'
        });
        return;
      }

      try {
        const payload = {
          metodo: this.salaEditando ? 'actualizar_sala' : 'crear_sala',
          sala: this.nuevaSala
        };

        if (this.salaEditando) {
          payload.sala.id = this.salaEditando.id;
        }

        await HttpService.registrar(payload, 'clases.php');

        this.$store.commit('mostrarMensaje', {
          texto: `Sala ${this.salaEditando ? 'actualizada' : 'creada'} exitosamente`,
          color: 'success'
        });

        this.cargarDatosIniciales();
        this.tabSalas = 0;
        this.resetFormSala();

      } catch (error) {
        console.error('Error guardando sala:', error);
        this.$store.commit('mostrarMensaje', {
          texto: 'Error al guardar sala',
          color: 'error'
        });
      }
    },

    async eliminarSala() {
      try {
        const payload = {
          metodo: 'eliminar_sala',
          id: this.salaSeleccionado.id
        };

        await HttpService.eliminar('clases.php', payload);

        this.$store.commit('mostrarMensaje', {
          texto: 'Sala eliminada exitosamente',
          color: 'success'
        });

        this.dialogEliminarSala = false;
        this.cargarDatosIniciales();

      } catch (error) {
        console.error('Error eliminando sala:', error);
        this.$store.commit('mostrarMensaje', {
          texto: 'Error al eliminar sala',
          color: 'error'
        });
      }
    },

    mostrarDisponibilidadFecha(fecha) {
      console.log('Fecha clickeada:', fecha);
    },

    async cargarDatosIniciales() {
      try {
        const [clasesRes, instructoresRes, salasRes] = await Promise.all([
          HttpService.obtenerConDatos({ metodo: 'obtener_clases' }, 'clases.php'),
          HttpService.obtenerConDatos({ metodo: 'obtener_instructores' }, 'clases.php'),
          HttpService.obtenerConDatos({ metodo: 'obtener_salas' }, 'clases.php')
        ]);

        this.clases = clasesRes;
        this.instructores = instructoresRes;
        this.salas = salasRes;
      } catch (error) {
        console.error('Error cargando datos iniciales:', error);
      }
    },

    async cargarHorarios() {
      try {
        this.cargando = true;
        const payload = {
          metodo: 'obtener_horarios',
          fecha_inicio: this.rangoFechas[0],
          fecha_fin: this.rangoFechas[1]
        };

        const respuesta = await HttpService.obtenerConDatos(payload, 'clases.php');

        this.horarios = respuesta.map(horario => {
          const start = this.crearFechaLocal(horario.fecha_hora_inicio);
          const end = this.crearFechaLocal(horario.fecha_hora_fin);

          return {
            id: horario.id,
            name: horario.nombre_clase,
            start: start,
            end: end,
            color: horario.color_calendario || '#1976D2',
            timed: true,
            extendedProps: {
              instructor: horario.nombre_instructor || 'Sin instructor',
              sala: horario.nombre_sala || 'Sin sala',
              cuposDisponibles: horario.cupos_disponibles || 0,
              cuposTotales: horario.max_participantes || 0,
              clase: horario.nombre_clase || 'Sin nombre',
              duracion: horario.duracion_min || 60,
              nivel: horario.nivel_dificultad || 'intermedio',
              precio: horario.precio || 0
            }
          };
        });

      } catch (error) {
        console.error('Error cargando horarios:', error);
        this.$store.commit('mostrarMensaje', {
          texto: 'Error al cargar horarios',
          color: 'error'
        });
      } finally {
        this.cargando = false;
      }
    },

    crearFechaLocal(fechaString) {
      if (!fechaString) return null;

      if (fechaString instanceof Date) return fechaString;

      // Formato MySQL: YYYY-MM-DD HH:mm:ss
      const [fecha, hora] = fechaString.split(' ');
      const [year, month, day] = fecha.split('-').map(Number);
      const [hours, minutes, seconds] = hora.split(':').map(Number);

      return new Date(
        year,
        month - 1,
        day,
        hours,
        minutes,
        seconds,
        0
      );
    }

    ,

    getEventColor(event) {
      return event.color;
    },

    mostrarDetalleEvento({ event }) {
      const extendedProps = event.extendedProps || {};

      this.eventoSeleccionado = {
        id: event.id,
        name: event.name,
        start: event.start,
        end: event.end,
        color: event.color,
        instructor: extendedProps.instructor || 'No especificado',
        sala: extendedProps.sala || 'No especificada',
        cuposDisponibles: extendedProps.cuposDisponibles || 0,
        cuposTotales: extendedProps.cuposTotales || 0,
        clase: extendedProps.clase || 'Sin nombre',
        duracion: extendedProps.duracion || 60,
        nivel: extendedProps.nivel || 'intermedio',
        precio: extendedProps.precio || 0
      };

      this.dialogDetalleClase = true;
    },

    formatoHora(fecha) {
      if (!fecha) return 'Hora no disponible';
      const fechaObj = fecha instanceof Date ? fecha : new Date(fecha);
      if (isNaN(fechaObj.getTime())) return 'Hora inválida';

      return fechaObj.toLocaleTimeString('es-MX', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    },

    async guardarClase() {
      if (this.nuevaClase.duracion_min < 15 || this.nuevaClase.duracion_min > 180) {
        this.$store.commit('mostrarMensaje', {
          texto: 'La duración debe estar entre 15 y 180 minutos',
          color: 'error'
        });
        return;
      }

      try {
        const payload = {
          metodo: 'crear_clase',
          clase: this.nuevaClase
        };

        await HttpService.registrar(payload, 'clases.php');
        this.dialogNuevaClase = false;
        this.cargarDatosIniciales();
        this.$store.commit('mostrarMensaje', {
          texto: 'Clase creada exitosamente',
          color: 'success'
        });
      } catch (error) {
        console.error('Error creando clase:', error);
      }
    },

    async guardarHorario() {
      try {
        if (!this.nuevoHorario.id_clase || !this.nuevoHorario.id_instructor || !this.nuevoHorario.id_sala) {
          this.$store.commit('mostrarMensaje', {
            texto: 'Seleccione clase, instructor y sala',
            color: 'warning'
          });
          return;
        }

        if (!this.nuevoHorario.fecha || !this.nuevoHorario.hora_inicio) {
          this.$store.commit('mostrarMensaje', {
            texto: 'Seleccione fecha y hora de inicio',
            color: 'warning'
          });
          return;
        }

        const claseSeleccionada = this.clases.find(c => c.id === this.nuevoHorario.id_clase);
        const duracionMinutos = claseSeleccionada ? claseSeleccionada.duracion_min : 60;

        const [hours, minutes] = this.nuevoHorario.hora_inicio.split(':');
        const [year, month, day] = this.nuevoHorario.fecha.split('-');

        const fechaHoraInicio = `${year}-${month}-${day} ${hours}:${minutes}:00`;
        const fechaHoraFin = this.sumarMinutosAFechaMySQL(fechaHoraInicio, duracionMinutos);

        if (!this.esFechaValida(fechaHoraInicio) || !this.esFechaValida(fechaHoraFin)) {
          throw new Error('Las fechas calculadas no son válidas');
        }

        const payload = {
          metodo: 'programar_horario',
          horario: {
            id_clase: this.nuevoHorario.id_clase,
            id_instructor: this.nuevoHorario.id_instructor,
            id_sala: this.nuevoHorario.id_sala,
            fecha_hora_inicio: fechaHoraInicio,
            fecha_hora_fin: fechaHoraFin,
            max_participantes: parseInt(this.nuevoHorario.max_participantes) || 20,
            repeticion: this.nuevoHorario.repeticion || 'ninguna',
            veces: parseInt(this.nuevoHorario.veces) || 1
          }
        };

        const resultado = await HttpService.registrar(payload, 'clases.php');

        if (resultado && resultado.error) {
          throw new Error(resultado.error);
        }

        let mensajeExito = 'Horario programado exitosamente';
        if (resultado && resultado.creados > 1) {
          mensajeExito = `${resultado.creados} horarios programados exitosamente`;
        }

        this.$store.commit('mostrarMensaje', {
          texto: mensajeExito,
          color: 'success'
        });

        this.dialogNuevoHorario = false;
        this.resetFormHorario();
        this.cargarHorarios();

      } catch (error) {
        console.error('Error programando horario:', error);
        this.$store.commit('mostrarMensaje', {
          texto: error.message || 'Error al programar horario',
          color: 'error'
        });
      }
    },

    sumarMinutosAFechaMySQL(fechaMySQL, minutos) {
      const [fechaPart, horaPart] = fechaMySQL.split(' ');
      const [year, month, day] = fechaPart.split('-');
      const [hours, minutes, seconds] = horaPart.split(':');

      const fecha = new Date(year, month - 1, day, hours, minutes, seconds);
      fecha.setMinutes(fecha.getMinutes() + parseInt(minutos));

      const pad = n => n.toString().padStart(2, '0');
      return `${fecha.getFullYear()}-${pad(fecha.getMonth() + 1)}-${pad(fecha.getDate())} ` +
        `${pad(fecha.getHours())}:${pad(fecha.getMinutes())}:${pad(fecha.getSeconds())}`;
    },

    esFechaValida(fechaMySQL) {
      const regex = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/;
      if (!regex.test(fechaMySQL)) return false;

      const [fechaPart, horaPart] = fechaMySQL.split(' ');
      const [year, month, day] = fechaPart.split('-').map(Number);
      const [hours, minutes, seconds] = horaPart.split(':').map(Number);

      if (year < 2000 || year > 2100) return false;
      if (month < 1 || month > 12) return false;
      if (day < 1 || day > 31) return false;
      if (hours < 0 || hours > 23) return false;
      if (minutes < 0 || minutes > 59) return false;

      return true;
    },

    resetFormHorario() {
      this.nuevoHorario = {
        id_clase: null,
        id_instructor: null,
        id_sala: null,
        fecha: new Date().toISOString().substr(0, 10),
        hora_inicio: null,
        max_participantes: 20,
        repeticion: 'ninguna',
        veces: 4
      };
    },

    async cargarMiembrosInscritos() {
      try {
        const payload = {
          metodo: 'obtener_miembros_inscritos',
          id_horario: this.eventoSeleccionado.id
        };

        this.miembrosInscritos = await HttpService.obtenerConDatos(payload, 'clases.php');
      } catch (error) {
        console.error('Error cargando miembros inscritos:', error);
        this.miembrosInscritos = [];
      }
    },

    async cargarMiembrosDisponibles() {
      try {
        const payload = {
          metodo: 'obtener_miembros_disponibles'
        };

        this.miembrosDisponibles = await HttpService.obtenerConDatos(payload, 'miembros.php');
      } catch (error) {
        console.error('Error cargando miembros disponibles:', error);
        this.miembrosDisponibles = [];
      }
    },

    async agregarMiembro() {
      if (!this.$refs.formMiembro.validate()) return;

      try {
        const payload = {
          metodo: 'agregar_miembro_clase',
          inscripcion: {
            id_horario: this.eventoSeleccionado.id,
            id_miembro: this.nuevoMiembro.id,
            monto_pagado: this.nuevoMiembro.monto_pagado || 0,
            fecha_inscripcion: new Date().toISOString().substr(0, 10)
          }
        };

        const resultado = await HttpService.registrar(payload, 'clases.php');

        if (resultado.exito) {
          this.$store.commit('mostrarMensaje', {
            texto: 'Miembro agregado exitosamente',
            color: 'success'
          });

          this.nuevoMiembro = { id: null, monto_pagado: 0 };
          this.$refs.formMiembro.reset();

          await this.cargarMiembrosInscritos();
          this.cargarHorarios();

        } else {
          throw new Error(resultado.mensaje);
        }

      } catch (error) {
        console.error('Error agregando miembro:', error);
        this.$store.commit('mostrarMensaje', {
          texto: error.message || 'Error al agregar miembro',
          color: 'error'
        });
      }
    },

    confirmarEliminarMiembro(miembro) {
      this.miembroSeleccionado = miembro;
      this.dialogEliminarMiembro = true;
    },

    async eliminarMiembro() {
      try {
        const payload = {
          metodo: 'eliminar_miembro_clase',
          id_inscripcion: this.miembroSeleccionado.id_inscripcion
        };

        const resultado = await HttpService.eliminar('clases.php', payload);
        const exito = (resultado.exito === true) || (resultado === true);

        if (exito) {
          this.$store.commit('mostrarMensaje', {
            texto: resultado.mensaje || 'Miembro eliminado de la clase',
            color: 'success'
          });

          this.dialogEliminarMiembro = false;
          await this.cargarMiembrosInscritos();
          await this.cargarHorarios();

        } else {
          throw new Error(resultado.mensaje || 'Error al eliminar miembro');
        }

      } catch (error) {
        console.error('Error eliminando miembro:', error);
        this.$store.commit('mostrarMensaje', {
          texto: error.message || 'Error al eliminar miembro',
          color: 'error'
        });
      }
    },

    formatoFecha(fecha) {
      if (!fecha) return '';
      const fechaObj = new Date(fecha);
      return fechaObj.toLocaleDateString('es-ES', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    }
  }
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@500;700&family=Inter:wght@400;500;600&family=JetBrains+Mono:wght@500&display=swap');

/* ===== TOKENS ===== */
.clases-page {
  --bg: #0E0E10;
  --card: #17171A;
  --card-hover: #1B1B1F;
  --card-border: #26262A;
  --text-primary: #F2F2ED;
  --text-secondary: #8C8C93;
  --text-tertiary: #5C5C62;
  --accent: #C8FF4D;
  --accent-dim: rgba(200, 255, 77, 0.12);
  --warning: #FFB84D;
  --error: #FF6B6B;
  --radius: 4px;

  --font-display: 'Space Grotesk', sans-serif;
  --font-body: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  --font-mono: 'JetBrains Mono', monospace;
}

.clases-page {
  min-height: 100vh;
  background: var(--bg);
  color: var(--text-primary);
  font-family: var(--font-body);
  position: relative;
  padding: 40px;
}

.bg-grid {
  position: fixed;
  inset: 0;
  background-image:
    linear-gradient(rgba(242, 242, 237, 0.02) 1px, transparent 1px),
    linear-gradient(90deg, rgba(242, 242, 237, 0.02) 1px, transparent 1px);
  background-size: 48px 48px;
  pointer-events: none;
  z-index: 0;
}

.clases-shell {
  position: relative;
  z-index: 1;
  max-width: 1200px;
  margin: 0 auto;
}

/* ===== HEADER ===== */
.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  padding: 22px 26px;
  background: var(--card);
  border: 1px solid var(--card-border);
  border-radius: var(--radius);
  margin-bottom: 20px;
}

.page-header-left {
  display: flex;
  align-items: center;
  gap: 16px;
}

.page-icon-box {
  width: 42px;
  height: 42px;
  border-radius: var(--radius);
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid var(--card-border);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.page-title {
  font-family: var(--font-display);
  font-size: 19px;
  font-weight: 700;
  margin: 0 0 2px;
}

.page-sub {
  font-size: 13px;
  color: var(--text-secondary);
  margin: 0;
}

.header-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

/* ===== CARD / CONTROLS ===== */
.card-flat {
  background: var(--card);
  border: 1px solid var(--card-border);
  border-radius: var(--radius);
}

.controls-card {
  padding: 14px 22px;
}

.calendar-controls {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
}

.calendar-nav {
  display: flex;
  align-items: center;
  gap: 8px;
}

.nav-btn {
  width: 32px;
  height: 32px;
  border-radius: var(--radius);
  background: transparent;
  border: 1px solid var(--card-border);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: border-color 0.15s ease, color 0.15s ease;
  color: var(--text-secondary);
}

.nav-btn:hover {
  border-color: var(--text-tertiary);
  color: var(--text-primary);
}

.calendar-date-display {
  display: flex;
  align-items: center;
  padding: 6px 12px;
  background: var(--accent-dim);
  border: 1px solid rgba(200, 255, 77, 0.3);
  border-radius: var(--radius);
}

.date-text {
  font-size: 12.5px;
  font-weight: 600;
  color: var(--accent);
  text-transform: capitalize;
}

.toggle-btn {
  margin-left: auto;
}

.calendar-card {
  padding: 20px;
  min-height: 600px;
}

/* ===== EYEBROW ===== */
.section-eyebrow {
  display: flex;
  align-items: center;
  gap: 10px;
  font-family: var(--font-mono);
  font-size: 11px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--text-secondary);
}

/* ===== MODAL ===== */
.modal-panel {
  background: var(--card);
  border: 1px solid var(--card-border);
  border-radius: var(--radius);
  overflow: hidden;
}

.modal-head {
  padding: 20px 24px;
  border-bottom: 1px solid var(--card-border);
}

.detail-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.event-color-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-top: 4px;
}

.modal-title {
  font-family: var(--font-display);
  font-size: 16px;
  font-weight: 700;
  margin: 0;
}

.modal-body {
  padding: 22px 24px;
}

.modal-text {
  font-size: 13.5px;
  color: var(--text-secondary);
  margin: 0;
  line-height: 1.6;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 16px 24px;
  border-top: 1px solid var(--card-border);
}

/* ===== TABS ===== */
.dark-tabs {
  background: transparent !important;
  border-bottom: 1px solid var(--card-border);
}

.dark-tabs >>> .v-tabs-bar {
  background: transparent !important;
}

.dark-tabs >>> .v-tab {
  color: var(--text-secondary) !important;
  font-family: var(--font-body) !important;
  text-transform: none !important;
  font-weight: 500 !important;
  letter-spacing: 0 !important;
  font-size: 13px !important;
}

.dark-tabs >>> .v-tab--active {
  color: var(--accent) !important;
}

.dark-tabs >>> .v-tabs-slider {
  background: var(--accent) !important;
}

.dark-tabs-items {
  background: transparent !important;
}

.tab-body {
  padding: 20px 24px;
}

/* ===== FIELDS ===== */
.dark-form {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.field-grow {
  flex: 2;
}

.field-label {
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.02em;
  color: var(--text-secondary);
  text-transform: uppercase;
}

.field-hint {
  font-size: 11px;
  color: var(--text-tertiary);
  margin: 4px 0 0;
}

.field-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.field-row-3 {
  grid-template-columns: 1fr 1fr 1fr;
}

.color-picker-wrapper {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
  background: var(--bg);
  border: 1px solid var(--card-border);
  border-radius: var(--radius);
}

.color-input {
  width: 32px;
  height: 32px;
  border: none;
  border-radius: var(--radius);
  cursor: pointer;
  background: transparent;
}

.color-preview {
  width: 24px;
  height: 24px;
  border-radius: var(--radius);
  border: 1px solid var(--card-border);
}

.color-value {
  font-family: var(--font-mono);
  font-size: 11px;
  color: var(--text-tertiary);
}

/* ===== TABLE ===== */
.table-wrapper {
  border: 1px solid var(--card-border);
  border-radius: var(--radius);
  overflow: hidden;
}

.plain-table {
  width: 100%;
  border-collapse: collapse;
}

.plain-table th {
  text-align: left;
  font-family: var(--font-mono);
  font-size: 10px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--text-tertiary);
  background: rgba(255, 255, 255, 0.02);
  padding: 10px 14px;
  border-bottom: 1px solid var(--card-border);
}

.plain-table td {
  padding: 10px 14px;
  border-bottom: 1px solid var(--card-border);
}

.plain-table tr:last-child td {
  border-bottom: none;
}

.cell-strong {
  font-size: 13px;
  font-weight: 600;
  color: var(--text-primary);
}

.cell-secondary {
  font-size: 12.5px;
  color: var(--text-secondary);
}

.empty-inline {
  padding: 24px;
  text-align: center;
  font-size: 12.5px;
  color: var(--text-tertiary);
}

/* ===== ICON BUTTONS ===== */
.icon-btn {
  width: 28px;
  height: 28px;
  border-radius: var(--radius);
  background: transparent;
  border: 1px solid transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease;
}

.icon-btn:hover {
  border-color: var(--card-border);
  background: rgba(255, 255, 255, 0.03);
}

.icon-btn-danger:hover {
  border-color: rgba(255, 107, 107, 0.3);
  background: rgba(255, 107, 107, 0.06);
}

/* ===== INSTRUCTORES ===== */
.instructores-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 12px;
}

.instructor-card {
  background: var(--bg);
  border: 1px solid var(--card-border);
  border-radius: var(--radius);
  padding: 16px;
}

.instructor-head {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
}

.instructor-avatar {
  width: 32px;
  height: 32px;
  border-radius: var(--radius);
  background: var(--accent);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.instructor-name {
  font-size: 14px;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0;
}

.instructor-specialty {
  display: flex;
  align-items: center;
  font-size: 11.5px;
  color: var(--text-tertiary);
  margin: 2px 0 0;
}

.instructor-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 10px 12px;
  background: rgba(255, 255, 255, 0.02);
  border-radius: var(--radius);
  margin-bottom: 12px;
}

.info-line {
  display: flex;
  align-items: center;
  font-size: 11.5px;
  color: var(--text-secondary);
}

.instructor-actions {
  display: flex;
  gap: 8px;
}

.btn-text-danger {
  background: transparent;
  border: 1px solid var(--card-border);
  color: var(--error);
  border-radius: var(--radius);
  padding: 7px 12px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  transition: border-color 0.15s ease, background 0.15s ease;
}

.btn-text-danger:hover {
  border-color: rgba(255, 107, 107, 0.3);
  background: rgba(255, 107, 107, 0.06);
}

/* ===== CLASE INFO GRID ===== */
.clase-info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 10px;
  margin-bottom: 16px;
}

.info-card {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px;
  background: var(--bg);
  border: 1px solid var(--card-border);
  border-radius: var(--radius);
}

.info-label {
  font-size: 10.5px;
  color: var(--text-tertiary);
}

.info-value {
  font-size: 12.5px;
  font-weight: 600;
  color: var(--text-primary);
}

/* ===== ALERT / STATUS ===== */
.alert-box {
  display: flex;
  align-items: center;
  gap: 10px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid var(--card-border);
  border-radius: var(--radius);
  padding: 12px 14px;
}

.alert-box-warning {
  background: rgba(255, 184, 77, 0.08);
  border-color: rgba(255, 184, 77, 0.25);
}

.alert-text {
  font-size: 12.5px;
  color: var(--text-secondary);
}

.alert-text strong {
  color: var(--text-primary);
}

.status-tag {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: var(--radius);
  font-size: 12px;
  font-weight: 600;
  font-family: var(--font-mono);
}

.status-tag-success {
  background: var(--accent-dim);
  color: var(--accent);
}

/* ===== EXPANSION PANELS ===== */
.dark-expansion {
  background: transparent !important;
}

.dark-expansion >>> .v-expansion-panel {
  background: var(--bg) !important;
  border: 1px solid var(--card-border) !important;
  border-radius: var(--radius) !important;
}

.dark-expansion >>> .v-expansion-panel::before {
  box-shadow: none !important;
}

.dark-expansion >>> .v-expansion-panel-header {
  color: var(--text-primary) !important;
  padding: 14px 16px !important;
}

.dark-expansion >>> .v-expansion-panel-header .v-icon {
  color: var(--text-tertiary) !important;
}

.dark-expansion >>> .v-expansion-panel-content__wrap {
  padding: 0 16px 16px !important;
}

.expansion-title {
  display: flex;
  align-items: center;
  font-size: 13px;
  font-weight: 600;
  color: var(--text-primary);
}

/* ===== MIEMBROS LIST ===== */
.miembros-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 320px;
  overflow-y: auto;
  margin-bottom: 4px;
}

.miembro-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  background: var(--bg);
  border: 1px solid var(--card-border);
  border-radius: var(--radius);
}

.miembro-avatar {
  width: 30px;
  height: 30px;
  border-radius: var(--radius);
  background: var(--accent);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.miembro-name {
  font-size: 13px;
  font-weight: 600;
  color: var(--text-primary);
}

.miembro-meta {
  font-size: 11px;
  color: var(--text-secondary);
  margin-top: 1px;
}

.miembro-date {
  font-size: 10.5px;
  color: var(--text-tertiary);
  margin-top: 1px;
}

.section-divider {
  height: 1px;
  background: var(--card-border);
  margin: 16px 0;
}

.add-member-row {
  display: flex;
  gap: 12px;
  align-items: flex-end;
  flex-wrap: wrap;
}

.add-member-row .field {
  min-width: 140px;
}

.add-member-btn {
  height: 42px;
}

.autocomplete-item-text {
  font-size: 13px;
  color: var(--text-primary);
}

/* ===== BUTTONS ===== */
.btn-primary {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: var(--accent);
  color: var(--bg);
  border: none;
  border-radius: var(--radius);
  padding: 10px 16px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: filter 0.15s ease;
}

.btn-primary:hover:not(:disabled) {
  filter: brightness(1.08);
}

.btn-primary:disabled {
  opacity: 0.45;
  cursor: default;
}

.btn-ghost {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  color: var(--text-secondary);
  border: 1px solid var(--card-border);
  border-radius: var(--radius);
  padding: 10px 16px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: border-color 0.15s ease, color 0.15s ease;
}

.btn-ghost:hover {
  border-color: var(--text-tertiary);
  color: var(--text-primary);
}

.btn-sm {
  padding: 7px 12px;
  font-size: 12px;
}

.btn-danger-solid {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: var(--error);
  color: #0E0E10;
  border: none;
  border-radius: var(--radius);
  padding: 10px 18px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: filter 0.15s ease;
}

.btn-danger-solid:hover {
  filter: brightness(1.1);
}

.text-error {
  color: var(--error);
}

/* ===== Overrides Vuetify (v-text-field, v-select, v-textarea, v-autocomplete) ===== */
.dark-input >>> .v-input__slot {
  background: var(--bg) !important;
  border-radius: 4px !important;
  box-shadow: none !important;
  min-height: 42px !important;
}

.dark-input >>> fieldset {
  border-color: var(--card-border) !important;
  border-width: 1px !important;
  border-radius: 4px !important;
  transition: border-color 0.15s ease;
}

.dark-input >>> .v-input--is-focused fieldset {
  border-color: var(--accent) !important;
  border-width: 1.5px !important;
}

.dark-input >>> input,
.dark-input >>> textarea,
.dark-input >>> .v-select__selection {
  color: var(--text-primary) !important;
  font-family: var(--font-body) !important;
  font-size: 13.5px !important;
  caret-color: var(--accent) !important;
}

.dark-input >>> input::placeholder,
.dark-input >>> textarea::placeholder {
  color: var(--text-tertiary) !important;
  opacity: 1 !important;
}

.dark-input >>> .v-text-field__prefix {
  color: var(--text-tertiary) !important;
  font-weight: 600 !important;
}

.dark-input >>> .v-icon {
  color: var(--text-tertiary) !important;
}

.dark-input >>> .v-messages {
  color: var(--error) !important;
}

/* Menús desplegables (select / autocomplete) */
.clases-page >>> .v-menu__content {
  background: var(--card) !important;
  border: 1px solid var(--card-border) !important;
  border-radius: 4px !important;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.5) !important;
}

.clases-page >>> .v-menu__content .v-list {
  background: transparent !important;
  padding: 4px !important;
}

.clases-page >>> .v-menu__content .v-list-item {
  border-radius: 4px !important;
}

.clases-page >>> .v-menu__content .v-list-item__title,
.clases-page >>> .v-menu__content .v-list-item__content {
  color: var(--text-primary) !important;
  font-family: var(--font-body) !important;
  font-size: 13px !important;
}

.clases-page >>> .v-menu__content .v-list-item:hover {
  background: rgba(255, 255, 255, 0.04) !important;
}

.clases-page >>> .v-menu__content .v-list-item--active {
  background: var(--accent-dim) !important;
}

.clases-page >>> .v-menu__content .v-list-item--active .v-list-item__title {
  color: var(--accent) !important;
}

/* v-date-picker / v-time-picker */
.dark-picker {
  background: var(--card) !important;
}

.dark-picker >>> .v-date-picker-header,
.dark-picker >>> .v-picker__body,
.dark-picker >>> .v-date-picker-table {
  background: var(--card) !important;
}

.dark-picker >>> .v-date-picker-header button,
.dark-picker >>> .v-date-picker-header .v-icon,
.dark-picker >>> .v-date-picker-table th {
  color: var(--text-primary) !important;
}

.dark-picker >>> .v-date-picker-table .v-btn,
.dark-picker >>> .v-time-picker-clock__item {
  color: var(--text-secondary) !important;
}

.dark-picker >>> .v-date-picker-table .v-btn.v-btn--active,
.dark-picker >>> .v-time-picker-clock__item--active {
  background: var(--accent) !important;
  color: var(--bg) !important;
}

.dark-picker >>> .v-time-picker-title,
.dark-picker >>> .v-time-picker-clock {
  background: var(--card) !important;
}

/* ===== Overrides v-calendar ===== */
.dark-calendar >>> .v-calendar-weekly {
  background: transparent !important;
  border-color: var(--card-border) !important;
}

.dark-calendar >>> .v-calendar-weekly__head-weekday {
  background: rgba(255, 255, 255, 0.02) !important;
  color: var(--text-tertiary) !important;
  font-family: var(--font-mono) !important;
  font-size: 10px !important;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  border-color: var(--card-border) !important;
}

.dark-calendar >>> .v-calendar-weekly__day {
  border-color: var(--card-border) !important;
  background: var(--bg) !important;
}

.dark-calendar >>> .v-calendar-weekly__day-label .v-btn {
  color: var(--text-primary) !important;
}

.dark-calendar >>> .v-calendar-weekly__day.v-outside {
  background: rgba(0, 0, 0, 0.2) !important;
}

.dark-calendar >>> .v-event {
  border-radius: 3px !important;
  font-size: 10.5px !important;
}

/* ===== MOTION ===== */
.fade-in {
  animation: fadeIn 0.4s ease both;
}

.delay-100 { animation-delay: 0.05s; }
.delay-200 { animation-delay: 0.1s; }

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@media (prefers-reduced-motion: reduce) {
  .fade-in { animation: none; }
}

/* ===== RESPONSIVE ===== */
@media (max-width: 768px) {
  .clases-page {
    padding: 20px;
  }

  .page-header {
    padding: 18px;
  }

  .header-actions {
    width: 100%;
  }

  .header-actions .btn-ghost,
  .header-actions .btn-primary {
    flex: 1;
  }

  .field-row,
  .field-row-3 {
    grid-template-columns: 1fr;
  }

  .instructores-grid {
    grid-template-columns: 1fr;
  }

  .clase-info-grid {
    grid-template-columns: 1fr;
  }

  .toggle-btn {
    margin-left: 0;
  }
}
</style>


// WEBPACK FOOTER //
// src/components/Clases/GestionClases.vue